import React, { useState } from "react";
import { Alert, Badge, Form, Spinner } from "react-bootstrap";
import dayjs from "dayjs";
import { BalanceCard } from "../components/BalanceCard";
import { PaymentDetailModal } from "../components/PaymentDetailModal";
import { useStudentBalance } from "../hooks/useStudentBalance";
import { useStudentPayments } from "../hooks/useStudentPayments";

const COP = (v) => `$${Number(v ?? 0).toLocaleString("es-CO")} COP`;

const STATUS_VARIANT = {
  PAID: "success",
  PENDING: "warning",
  VOID: "secondary",
  CANCELLED: "danger",
};

/**
 * Combined student money view: hour balance on top, payment history below.
 * Clicking a payment opens a modal with the associated package detail (the
 * per-package info is no longer shown inline in the main view).
 */
function MyBalancePage() {
  const { data: balance, isLoading: balanceLoading, isError: balanceError } =
    useStudentBalance();
  const { data: payments = [], isLoading: paymentsLoading } =
    useStudentPayments();

  const [statusFilter, setStatusFilter] = useState("ALL");
  const [selected, setSelected] = useState(null);

  const filtered =
    statusFilter === "ALL"
      ? payments
      : payments.filter((p) => p.status === statusFilter);

  return (
    <div className="container-fluid py-4" style={{ maxWidth: 860 }}>
      <h4 className="mb-3">Mi saldo</h4>

      {/* ── Saldo de horas ─────────────────────────────────────────────── */}
      {balanceLoading && (
        <div className="text-center py-4">
          <Spinner animation="border" size="sm" />
        </div>
      )}
      {balanceError && (
        <Alert variant="danger">Error al cargar tu saldo de horas.</Alert>
      )}
      {balance?.has_frozen && (
        <Alert variant="danger" className="mb-3">
          Tienes un paquete con <strong>saldo pendiente vencido</strong>
          {balance.amount_due
            ? ` ($${Number(balance.amount_due).toLocaleString("es-CO")})`
            : ""}
          . No podrás agendar clases hasta saldarlo. Contacta a tu academia.
        </Alert>
      )}
      {!balance?.has_frozen && balance?.has_withheld && (
        <Alert variant="warning" className="mb-3">
          Tu paquete tiene un saldo pendiente. Las horas se activarán cuando
          completes el pago.
        </Alert>
      )}
      {balance && (
        <div className="row mb-4">
          <div className="col-12 col-md-7">
            <BalanceCard
              balance={balance}
              onRenew={() =>
                alert("Contacta a tu academia para renovar tu paquete de horas.")
              }
            />
          </div>
        </div>
      )}

      {/* ── Mis pagos ──────────────────────────────────────────────────── */}
      <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-2">
        <h5 className="mb-0">Mis pagos</h5>
        <Form.Select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          style={{ maxWidth: 160 }}
          aria-label="Filtrar por estado"
        >
          <option value="ALL">Todos</option>
          <option value="PAID">Pagados</option>
          <option value="PENDING">Pendientes</option>
          <option value="VOID">Anulados</option>
        </Form.Select>
      </div>
      <p className="text-muted small mb-2">
        Toca un pago para ver el detalle del paquete.
      </p>

      {paymentsLoading && (
        <div className="text-center py-4">
          <Spinner animation="border" size="sm" />
        </div>
      )}

      {!paymentsLoading && filtered.length === 0 && (
        <Alert variant="info">No hay pagos que coincidan con el filtro.</Alert>
      )}

      {/* Desktop table */}
      {filtered.length > 0 && (
        <div className="d-none d-sm-block table-responsive">
          <table className="table table-hover align-middle">
            <thead>
              <tr>
                <th>Recibo #</th>
                <th>Fecha</th>
                <th>Monto</th>
                <th>Método</th>
                <th>Período</th>
                <th>Estado</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((p) => (
                <tr
                  key={p.id}
                  style={{ cursor: "pointer" }}
                  onClick={() => setSelected(p)}
                >
                  <td className="fw-semibold">{p.receipt_number || "—"}</td>
                  <td>{dayjs(p.paid_at).format("DD/MM/YYYY")}</td>
                  <td className="text-success fw-semibold">{COP(p.amount)}</td>
                  <td>{p.method_display ?? p.method}</td>
                  <td>{p.period}</td>
                  <td>
                    <Badge bg={STATUS_VARIANT[p.status] ?? "secondary"}>
                      {p.status_display ?? p.status}
                    </Badge>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Mobile cards */}
      {filtered.length > 0 && (
        <div className="d-block d-sm-none">
          {filtered.map((p) => (
            <button
              key={p.id}
              className="card mb-3 w-100 text-start border-0 shadow-sm"
              onClick={() => setSelected(p)}
            >
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-start">
                  <div>
                    <div className="fw-semibold text-success">
                      {COP(p.amount)}
                    </div>
                    <div className="text-muted small">
                      Recibo #{p.receipt_number || "—"} ·{" "}
                      {dayjs(p.paid_at).format("DD/MM/YYYY")}
                    </div>
                    <div className="text-muted small">
                      {p.method_display ?? p.method}
                    </div>
                  </div>
                  <Badge bg={STATUS_VARIANT[p.status] ?? "secondary"}>
                    {p.status_display ?? p.status}
                  </Badge>
                </div>
              </div>
            </button>
          ))}
        </div>
      )}

      <PaymentDetailModal
        show={Boolean(selected)}
        onHide={() => setSelected(null)}
        payment={selected}
      />
    </div>
  );
}

export { MyBalancePage };
